import axios from "axios";
import { customLogout } from "./features/User/userSlice";
import { sessionDebugEnabled } from "../utils/sessionDebug";

const axiosInstance = axios.create({
  baseURL: `/api/v1`,
  withCredentials: true,
});

var storeModule;
var store;

let isRefreshing = false;
let failedQueue = [];

const REFRESH_LOCK = "healink:refresh";

const withRefreshLock = (fn) =>
  navigator.locks?.request ? navigator.locks.request(REFRESH_LOCK, fn) : fn();

const TAB_ID = (() => {
  try {
    const existing = sessionStorage.getItem("healink:debugTabId");
    if (existing) return existing;
    const id = Math.random().toString(36).slice(2, 8);
    sessionStorage.setItem("healink:debugTabId", id);
    return id;
  } catch {
    return Math.random().toString(36).slice(2, 8);
  }
})();

const sessionLog = (event, details) => {
  if (!sessionDebugEnabled()) return;
  console.info(
    `[session ${TAB_ID}] ${new Date().toISOString()} ${event}`,
    details ?? ""
  );
};

const processQueue = (error) => {
  sessionLog("queue:flush", { queued: failedQueue.length, failed: !!error });
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

const initializeAxiosInterceptors = async () => {
  if (!storeModule) {
    storeModule = await import("./configureStore");
  }

  store = storeModule.store;

  axiosInstance.interceptors.request.use(
    (config) => {
      const hasFiles = config.data instanceof FormData;

      if (hasFiles) {
        config.maxBodyLength = Infinity;
        config.maxContentLength = Infinity;
      } else {
        config.headers["Content-Type"] = "application/json";
        config.headers["Accept"] = "application/json";
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url.includes("/auth/refresh") &&
        !originalRequest.url.includes("/auth/logout")
      ) {
        sessionLog("401:intercepted", {
          url: originalRequest.url,
          refreshAlreadyInFlight: isRefreshing,
        });
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(() => axiosInstance(originalRequest));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          await withRefreshLock(() => axiosInstance.post("/auth/refresh"));
          sessionLog("401:refresh-ok", { retrying: originalRequest.url });
          processQueue(null);
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          sessionLog("401:refresh-failed -> LOGOUT", {
            url: originalRequest.url,
            status: refreshError?.response?.status,
            message:
              refreshError?.response?.data?.data?.message ||
              refreshError?.message,
          });
          processQueue(refreshError);
          const expiredRole = store.getState()?.user?.user?.role;
          store.dispatch(customLogout());
          // Session expiry lands on the role's own sign-in, carrying the
          // current location so login returns the user to where they were.
          // Dynamic import: same circular-dependency dance as configureStore.
          if (expiredRole) {
            const { redirectToRoleSignIn } = await import(
              "../utils/signInRedirect"
            );
            redirectToRoleSignIn(
              expiredRole,
              `${window.location.pathname}${window.location.search}`
            );
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      if (error.response?.status === 401) {
        sessionLog("401:not-retried", {
          url: originalRequest?.url,
          alreadyRetried: !!originalRequest?._retry,
        });
      }

      return Promise.reject(error);
    }
  );

  sessionLog("init", { loggedIn: !!store.getState()?.user?.user });
};

initializeAxiosInterceptors();

export default axiosInstance;
